import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SquarePen, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import ListView from "../../shared/ListView";
import ConfirmModal from "../../shared/ConfirmModal";
import {
  useGetPremiumDepositEntryQuery,
  useDeletePremiumDepositEntryMutation,
  useBulkDeletePremiumDepositEntryMutation,
} from "../../../../services/redux/api/components";

const PremiumDepositEntryList = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);

  const [deleteSingle] = useDeletePremiumDepositEntryMutation();
  const [bulkDelete] = useBulkDeletePremiumDepositEntryMutation();

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteMode, setDeleteMode] = useState("single");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNames, setSelectedNames] = useState([]);
  const selectedIdsRef = useRef([]);
  const [allSelected, setAllSelected] = useState(false);
  const [preserveSelection, setPreserveSelection] = useState(false);

  // Filter & sort
  const [filterColumn, setFilterColumn] = useState("PolicyNumber");
  const [filterValue, setFilterValue] = useState("");
  const [columnOptions, setColumnOptions] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [columnDefs, setColumnDefs] = useState([]);

  const { data: pdEntries, isLoading, error, refetch } = useGetPremiumDepositEntryQuery({
    pageNumber: currentPage,
    pageSize: itemsPerPage,
    sortField,
    sortOrder: sortDirection,
    ...(isFilterApplied && {
      searchTerm: filterColumn,
      searchValue: filterValue,
    }),
  });

  useEffect(() => {
    const options = [
      { value: "PolicyNumber", label: "Policy Number" },
      { value: "PaymentMode", label: "Payment Mode" },
    ];
    setColumnOptions(options);
  }, []);

  // --- ROW DATA: derive simple YYYY-MM-DD via slice(0,10) ---
  const rowData = useMemo(() => {
    if (!pdEntries?.data) return [];
    return Array.isArray(pdEntries.data)
      ? pdEntries.data.map((item) => {
          const id = item.DepositId ?? item.id;
          const dueIso = item?.DueDate ?? null;
          const payIso = item?.PaymentDate ?? null;

          // defensive slice -> 'YYYY-MM-DD'
          const dueYmd = dueIso ? String(dueIso).slice(0, 10) : "";
          const payYmd = payIso ? String(payIso).slice(0, 10) : "";

          return {
            ...item,
            id,
            _rawDueDate: dueIso,
            _rawPaymentDate: payIso,
            DueDateDisplay: dueYmd || "-",
            PaymentDateDisplay: payYmd || "-",
            DueDateInput: dueYmd, // ready for <input type="date">
            PaymentDateInput: payYmd,
          };
        })
      : [];
  }, [pdEntries]);

  const handleFilter = () => {
    if (!filterValue.trim()) return toast.warning("Please enter filter value");
    setIsFilterApplied(true);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterValue("");
    setIsFilterApplied(false);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    let direction = "asc";
    if (sortField === field) {
      direction = sortDirection === "asc" ? "desc" : "asc";
    }
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const createHeaderComponent = (field, displayName) => {
    return function CustomHeader() {
      const isSorted = sortField === field;
      const isAsc = sortDirection === "asc";

      return (
        <div className="cursor-pointer hover:text-brandRed flex items-center" onClick={() => handleSort(field)}>
          {displayName}
          {isSorted && <span className="ml-1">{isAsc ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>}
        </div>
      );
    };
  };

  // --- column defs (use Display fields for dates) ---
  useEffect(() => {
    const createColumnDefs = () => [
      { headerCheckboxSelection: true, checkboxSelection: true, width: 50, suppressMenu: true },
      {
        headerName: "Policy Number",
        field: "PolicyNumber",
        flex: 1,
        headerComponent: createHeaderComponent("PolicyNumber", "Policy Number"),
        suppressMenu: true,
      },
      {
        headerName: "Due Date",
        field: "DueDateDisplay",
        flex: 1,
        headerComponent: createHeaderComponent("DueDate", "Due Date"),
        valueGetter: (params) => params.data?.DueDateDisplay ?? "-",
        suppressMenu: true,
      },
      {
        headerName: "Payment Date",
        field: "PaymentDateDisplay",
        flex: 1,
        headerComponent: createHeaderComponent("PaymentDate", "Payment Date"),
        valueGetter: (params) => params.data?.PaymentDateDisplay ?? "-",
        suppressMenu: true,
      },
      {
        headerName: "Premium Amount",
        field: "PremiumAmount",
        flex: 1,
        headerComponent: createHeaderComponent("PremiumAmount", "Premium Amount"),
        suppressMenu: true,
      },
      {
        headerName: "Action",
        field: "action",
        headerClass: "action-header",
        cellRenderer: (params) => (
          <div className="flex space-x-2 mt-1">
            <button
              className="edit-button"
              onClick={() =>
                navigate(`/premium-deposit-entry/edit/${params.data.DepositId || params.data.id}`, {
                  state: { row: params.data }, // optional: pass row so edit form can prefill without refetch
                })
              }
              title="Edit"
              onMouseDown={(e) => e.preventDefault()}
            >
              <SquarePen color="#787474" size={18} className="mt-1" />
            </button>
            <button
              className="delete-button"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteId(params.data.DepositId || params.data.id);
                setDeleteMode("single");
                onOpen();
              }}
              title="Delete"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Trash2 color="#787474" size={18} className="mt-1" />
            </button>
          </div>
        ),
        sortable: false,
      },
    ];

    setColumnDefs(createColumnDefs());
  }, [sortField, sortDirection, navigate]);

  const onSelectionChanged = () => {
    if (isOpen || !gridRef.current?.api) return;
    try {
      const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
      const totalDisplayedRows = gridRef.current.api.getDisplayedRowCount() || 0;

      selectedIdsRef.current = selectedNodes.map((node) => node.data.DepositId || node.data.id);
      setSelectedNames(selectedNodes.map((node) => node.data.PolicyNumber || node.data.DepositId || node.data.id));

      setAllSelected(selectedNodes.length === totalDisplayedRows && totalDisplayedRows > 0);
      setDeleteMode(selectedNodes.length > 1 || allSelected ? "multi" : "single");
    } catch (error) {
      console.error("Error in onSelectionChanged:", error);
    }
  };

  const onOpen = () => {
    if (!gridRef.current?.api) return;
    try {
      const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
      selectedIdsRef.current = selectedNodes.map((node) => node.data.DepositId || node.data.id);
      setSelectedNames(selectedNodes.map((node) => node.data.PolicyNumber || node.data.DepositId || node.data.id));
      setPreserveSelection(true);
      setIsOpen(true);
    } catch (error) {
      console.error("Error in onOpen:", error);
    }
  };

  const onClose = () => {
    setIsOpen(false);
    setAllSelected(false);
    setPreserveSelection(false);
  };

  const handleItemsPerPageChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setItemsPerPage(newPageSize);
    setCurrentPage(1);
  };

  const handleDeleteClick = () => {
    const count = selectedIdsRef.current.length;
    if (count === 0) return toast.warning("Please select at least one entry to delete");
    setDeleteMode(allSelected || count > 1 ? "multi" : "single");
    setDeleteId(allSelected ? null : selectedIdsRef.current[0]);
    setIsOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteMode === "single" && deleteId) {
        const response = await deleteSingle(deleteId).unwrap();
        if (response?.success) {
          toast.success(response.message || "Deleted successfully");
          refetch();
        } else {
          toast.error(response?.message || "Failed to delete");
        }
      } else {
        const idsToDelete = allSelected ? rowData.map((x) => x.DepositId || x.id) : selectedIdsRef.current;
        const response = await bulkDelete({ ids: idsToDelete }).unwrap();
        if (response?.success) {
          toast.success(response.message || `${idsToDelete.length} items deleted successfully`);
          refetch();
        } else {
          toast.error(response?.message || "Failed to delete items");
        }
      }
    } catch (err) {
      toast.error(err?.data?.message || "Something went wrong");
    } finally {
      onClose();
    }
  };

  useEffect(() => {
    const totalPages = Math.ceil(pdEntries?.totalCount / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(totalPages);
  }, [pdEntries?.totalCount, currentPage, itemsPerPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= Math.ceil(pdEntries?.totalCount / itemsPerPage)) setCurrentPage(newPage);
  };

  return (
    <div className="container p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-brandRed">Premium Deposit Entry List</h2>
        <div className="flex gap-2">
          <button className="px-4 py-2 addListTable-button" onClick={() => navigate("/premium-deposit-entry")}>
            Add
          </button>
          {selectedNames.length > 0 && (
            <button className="px-4 py-2 addListTable-button" onClick={handleDeleteClick}>
              Delete
            </button>
          )}
        </div>
      </div>

      <ListView
        filterColumn={filterColumn}
        setFilterColumn={setFilterColumn}
        columnOptions={columnOptions}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        handleFilter={handleFilter}
        clearFilters={clearFilters}
        isFilterApplied={isFilterApplied}
        gridRef={gridRef}
        columnDefs={columnDefs}
        rowData={rowData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={handlePageChange}
        onSelectionChanged={onSelectionChanged}
        defaultColDef={{ sortable: true, resizable: true, suppressMenu: true }}
        loading={isLoading}
        handleItemsPerPageChange={handleItemsPerPageChange}
        totalCount={pdEntries?.totalCount || 0}
      />

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message={
          allSelected
            ? `Are you sure you want to delete all ${rowData.length} entries on this page?`
            : deleteMode === "multi"
            ? `Are you sure you want to delete ${selectedIdsRef.current.length} selected entries?`
            : `Are you sure you want to delete this entry: ${selectedNames[0]}?`
        }
        selectedNames={selectedNames}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="backButton"
      />
    </div>
  );
};

export default PremiumDepositEntryList;
